import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronUp, ChevronDown, Search, Loader2 } from 'lucide-react';
import { useADStore } from '../../store/adStore';
import { fetchBloodHoundQueries, runBloodHoundQuery, fetchTenants } from '../../services/adApi';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useReactFlow, useStore } from '@xyflow/react';
import { ZoomControls } from '../ZoomControls';

export function QueryToolbar() {
  const {
    selectedTenant,
    queries,
    setQueries,
    selectedQuery,
    setSelectedQuery,
    setSelectedTenant,
    isLoadingQueries,
    setLoadingQueries,
    loadGraph,
    isLoadingGraph,
    selectedNode,
  } = useADStore();
  const inspectorOpen = Boolean(selectedNode && selectedTenant);

  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Close panel on any click outside the toolbar (capture phase to beat React Flow)
  useEffect(() => {
    if (!expanded) return;
    const handler = (e: PointerEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [expanded]);

  const { setViewport, getViewport } = useReactFlow();
  const zoom = useStore((s) => s.transform[2]);

  const handleZoomChange = useCallback(
    (newZoom: number) => {
      const viewport = getViewport();
      setViewport({ ...viewport, zoom: newZoom });
    },
    [getViewport, setViewport]
  );

  // Fetch queries when tenant changes
  useEffect(() => {
    if (!selectedTenant) return;

    const load = async () => {
      setLoadingQueries(true);
      try {
        const data = await fetchBloodHoundQueries(selectedTenant);
        setQueries(data);
      } catch (err) {
        console.error('Failed to fetch queries:', err);
        toast.error('Failed to load BloodHound queries');
        setQueries([]);
      } finally {
        setLoadingQueries(false);
      }
    };
    load();
  }, [selectedTenant, setQueries, setLoadingQueries]);

  // Run selected query
  const handleSelectQuery = useCallback(async (query: typeof queries[0]) => {
    let tenantToUse = selectedTenant;

    if (!tenantToUse) {
      try {
        const tenants = await fetchTenants();
        if (tenants && tenants.length > 0) {
          tenantToUse = tenants[0].tenantName;
          setSelectedTenant(tenantToUse);
        } else {
          toast.error('No tenant available to run query');
          return;
        }
      } catch (err) {
        console.error('Failed to fetch tenants:', err);
        toast.error('Failed to resolve tenant');
        return;
      }
    }

    setSelectedQuery(query);
    setExpanded(false);

    const tenantName = tenantToUse;
    try {
      const loaded = await loadGraph((signal) => runBloodHoundQuery(query.query, tenantName, query.includeProperties, signal));
      if (loaded) toast.success(`Loaded: ${query.description}`);
    } catch (err) {
      console.error('Failed to run query:', err);
      toast.error('Failed to run query');
    }
  }, [selectedTenant, setSelectedTenant, setSelectedQuery, loadGraph]);


  const filteredQueries = useMemo(() => {
    if (!searchQuery) return queries;
    return queries.filter(q =>
      q.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [queries, searchQuery]);

  return (
    <div
      ref={toolbarRef}
      className="fixed bottom-6 z-50 w-full max-w-2xl px-4"
      style={{
        left: inspectorOpen ? 'calc(50% + 8rem - 232px)' : 'calc(50% + 8rem)',
        maxWidth: inspectorOpen ? 'min(42rem, calc(100% - 16rem - 512px))' : undefined,
        transform: 'translateX(-50%)',
        transition: 'left 300ms cubic-bezier(0.22, 1, 0.36, 1), max-width 300ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {/* Expanded panel */}
      <div
        className={cn(
          "transition-all duration-300 ease-out overflow-hidden rounded-2xl",
          expanded ? "max-h-[60vh] opacity-100 mb-3 shadow-2xl" : "max-h-0 opacity-0 shadow-none"
        )}
      >
        <div className="bg-card border border-border/40 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="px-4 pt-4 pb-3 border-b border-border/30">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <span className="text-sm font-semibold text-foreground tracking-wide">
                  Queries
                </span>
              </div>
              <span className="text-[11px] font-medium text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full">
                {filteredQueries.length} {filteredQueries.length === 1 ? 'query' : 'queries'}
              </span>
            </div>

            {/* Search */}
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                placeholder="Search queries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-2.5 rounded-xl bg-background/60 border border-border/50 text-sm outline-none text-foreground placeholder:text-muted-foreground/60 focus:border-primary/40 focus:ring-2 focus:ring-primary/10 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-muted-foreground/20 hover:bg-muted-foreground/30 flex items-center justify-center text-muted-foreground transition-colors"
                >
                  <span className="text-[10px] leading-none">✕</span>
                </button>
              )}
            </div>
          </div>

          {/* Query list */}
          <div className="overflow-y-auto max-h-[40vh] custom-scrollbar">
            {isLoadingQueries ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-primary/60" />
                <span className="text-xs text-muted-foreground">Loading queries…</span>
              </div>
            ) : filteredQueries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <Search className="w-5 h-5 text-muted-foreground/40" />
                <span className="text-sm text-muted-foreground">No queries found</span>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-xs text-primary hover:underline"
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div className="p-1.5">
                {filteredQueries.map((query, i) => {
                  const isActive = selectedQuery?.description === query.description;

                  return (
                    <button
                      key={i}
                      onClick={() => handleSelectQuery(query)}
                      disabled={isLoadingGraph}
                      className={cn(
                        "w-full text-left px-3.5 py-3 rounded-xl text-sm transition-all duration-150 flex items-center gap-3 group",
                        "hover:bg-primary/[0.06] disabled:opacity-40 disabled:pointer-events-none",
                        isActive
                          ? "bg-primary/10 text-primary font-medium shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.2)]"
                          : "text-foreground/80 hover:text-foreground"
                      )}
                    >
                      {/* Number indicator */}
                      <span
                        className={cn(
                          "flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-semibold transition-colors",
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted/60 text-muted-foreground group-hover:bg-primary/15 group-hover:text-primary"
                        )}
                      >
                        {i + 1}
                      </span>

                      {/* Query description */}
                      <span className="truncate flex-1">{query.description}</span>

                      {/* Active check */}
                      {isActive && (
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom floating bar */}
      <div className="bg-card/70 backdrop-blur-xl border border-border/50 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-2">
        <div className="flex items-center gap-4 px-2">
          {/* Toggle button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary text-sm font-semibold transition-all duration-200"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            Queries
          </button>

          {/* Vertical divider */}
          <div className="w-px h-6 bg-border/50" />

          {/* Current query name */}
          <div className="flex-1 min-w-0 text-center">
            {selectedQuery ? (
              <span className="text-sm font-medium text-foreground truncate block">{selectedQuery.description}</span>
            ) : (
              <span className="text-sm text-muted-foreground/60">Select a query to visualize</span>
            )}
          </div>

          {/* Loading indicator */}
          {isLoadingGraph && (
            <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
          )}

          {/* Zoom Controls */}
          <div className="hidden sm:block flex-shrink-0">
            <ZoomControls
              zoom={zoom}
              minZoom={0.04}
              maxZoom={4}
              onZoomChange={handleZoomChange}
              className="bg-transparent border-none shadow-none p-0"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
